/**
 * ReportDocument — ONE TAB, as a page with nothing on it you can press.
 *
 * TWO DOCUMENTS, NOT ONE (2026-08-21, Jose: "1 por tab tiene más sentido"). The
 * first version welded both tabs into a single file. It read as two reports
 * stapled together, and it made the provenance block incoherent: the candidate
 * filters and the progress range are different filters over different data, and
 * one footer trying to describe both said neither clearly. `section` picks which
 * document this is; each carries only its own content and only its own applied
 * state.
 *
 * THE POINT OF THIS FILE IS THAT IT REUSES THE PAGE'S OWN COMPONENTS. The charts
 * here are the charts on screen — same `StageDonut`, same `StageLines`, same
 * palette, same arithmetic. That is the entire reason the HTML export moved into
 * the browser: a second set of renderers (Python drawing SVG, say) would be six
 * charts existing twice, and the two copies would diverge the first time one was
 * touched. Reusing the components makes "the export mirrors the dashboard" true
 * by construction rather than by anybody remembering.
 *
 * WHAT IS DELIBERATELY ABSENT: every control. No filter inputs, no sortable
 * headers, no pager, no tab strip, no range picker, no export menu, no Refresh.
 * The document runs no JavaScript, so a control here would be a thing that looks
 * pressable and does nothing — worse than its absence. Where a control carried
 * INFORMATION (which range, which granularity, which filters), that information
 * is restated as prose instead.
 *
 * THE CHARTS ARE PASSED AN EXPLICIT WIDTH, and that is not a detail.
 * `useElementWidth` measures in an effect; `renderToStaticMarkup` runs no
 * effects and the downloaded file runs no JS at all. Left alone, every chart
 * would fall back to its 480-unit viewBox and then scale by
 * `containerWidth / 480` in whatever browser opened the file — which is exactly
 * the font-scaling bug the measured width was introduced to fix, reappearing in
 * the export. `DOCUMENT_WIDTH` is passed to the charts AND pins the page's own
 * content width, so the two cannot disagree.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchCandidateRow, SearchReport, SearchReportHeader } from "@nu-assist/contracts";

import { ReportPanel } from "../ReportPanel";
import { SearchDetailKpis } from "../SearchDetailKpis";
import { AdditionsLine } from "../charts/AdditionsLine";
import { FunnelBars } from "../charts/FunnelBars";
import { LabeledBars } from "../charts/LabeledBars";
import { OutreachLines } from "../charts/OutreachLines";
import { StageDonut } from "../charts/StageDonut";
import { StageLines } from "../charts/StageLines";
import { titleCaseNoun } from "../charts/chartTitles";
import { type ProgressView, describeProgressView } from "../charts/progressView";
import { bucketLabel, bucketOutreach } from "../charts/timeRange";

/**
 * The document's content width, in CSS pixels, and the width every chart is
 * drawn at.
 *
 * 960 rather than a page-relative unit because a chart's viewBox needs a NUMBER
 * at render time — there is no layout pass to ask. 960 fits A4 portrait at
 * ordinary print margins and reads comfortably on screen.
 */
export const DOCUMENT_WIDTH = 960;

/** Half the content width less the gap — what a two-column panel gets. */
const HALF_WIDTH = (DOCUMENT_WIDTH - 16) / 2 - 32;

export interface AppliedFilterSummary {
  label: string;
  value: string;
}

/** Which tab this document is. */
export type ReportSection = "dashboard" | "progress";

export interface ReportDocumentProps {
  section: ReportSection;
  search: SearchReportHeader;
  report: SearchReport;
  /** Every candidate the export asked for — see `?all=true`. */
  candidates: readonly SearchCandidateRow[];
  /** True when the projection holds fewer rows than the search has. */
  candidatesTruncated?: boolean;
  /** Whether the report was past its daily refresh when this was taken. */
  stale?: boolean;
  /**
   * The filters in force, or empty for the unfiltered "Full report".
   *
   * Rendered as prose in the provenance block. A downloaded file outlives the
   * screen it was taken from, and "why does this have 34 rows" is unanswerable
   * a week later unless the file says so itself.
   */
  appliedFilters?: readonly AppliedFilterSummary[];
  /**
   * The Search Progress view this document should draw — the SAME object the tab
   * computes, not a re-derivation.
   *
   * Required for `section: "progress"`. The first version rebuilt the progress
   * charts from the raw report and silently ignored `?from`, `?to` and
   * `?bucket`, so narrowing the range to April downloaded a document covering
   * the whole year. Taking the view as an argument is what makes that
   * impossible rather than merely fixed.
   */
  progress?: ProgressView;
  /** When the document was produced. Passed in — this component is pure. */
  generatedOn: string;
  /**
   * Whether the candidates table prints the Candidates tab's columns —
   * Location, Rejection reason, In stage since (2026-10-06).
   *
   * Gated on the SAME visibility as the tab (the `search-candidates` flag,
   * released on every stage since 2026-10-07), and false by default, so that
   * a build where the tab is off downloads the document it always did: the
   * export menu lives on the Dashboard toolbar, and the first version printed
   * the new columns there unconditionally while the PR promised prod would see
   * nothing new (review iter 1, Agent C #2).
   */
  candidateDetails?: boolean;
}

/** `2026-07-06T12:00:00Z` → `Jul 6, 2026`; anything unparseable → em dash. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ReportDocument({
  section,
  search,
  report,
  candidates,
  candidatesTruncated = false,
  stale = false,
  appliedFilters = [],
  progress,
  generatedOn,
  candidateDetails = false,
}: ReportDocumentProps) {
  return (
    <div className="mx-auto flex flex-col gap-4 bg-bg p-6 text-text" data-testid="report-document">
      <header className="flex flex-col gap-1">
        <h1 className="text-h1 text-text">{search.name}</h1>
        <p className="text-body text-text-secondary">
          {[
            search.client_company_name,
            search.status,
            search.days_open === null || search.days_open === undefined
              ? null
              : `${search.days_open} days open`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <p className="text-caption text-text-secondary" data-testid="document-section">
          {/* Named for the TAB, which reads "Search Progress"
              (`SearchDetailPage.tsx`), and sitting directly above headings that
              went Title Case on 2026-09-01. It was the last lowercase heading
              in a document that no longer has any. */}
          {section === "dashboard" ? "Dashboard" : "Search Progress"}
        </p>
      </header>

      {section === "dashboard" ? (
        <>
          <SearchDetailKpis kpis={report.kpis} />

          <div className="grid grid-cols-2 gap-4">
            {/* THE SUBTITLE CARRIES THE DISCLOSURE, because this document has
                nowhere else to put it. The live tab explains the filter in a
                `Panel` tooltip and the server-rendered formats carry a basis
                line plus a metadata row; a static document renders no JS, so an
                info icon here would be a control that never opens. Without this
                the `.html` — the surface most likely to reach a client — showed
                a ring summing to a population it never named, under a KPI card
                reading a larger number. B2.1/N2, iter-2 review of PR #797. */}
            <ReportPanel
              title="Current Stage"
              subtitle="Where candidates are now, excluding Source and Jury Member — they are not candidates on the search"
            >
              {/* Unfiltered wire value in, filtered ring out — `StageDonut`
                  owns the not-a-candidate rule and derives its own hole. This
                  call site passed `total_candidacies` until 2026-08-31 and was
                  the surface the first cut of that change missed entirely. */}
              <StageDonut buckets={report.current_stage_distribution} />
            </ReportPanel>
            <ReportPanel
              title="Highest Stage Funnel"
              subtitle="Furthest stage reached, excluding Source and Jury Member — they are not candidates on the search"
            >
              <FunnelBars
                steps={report.highest_stage_funnel}
                emptyMessage="No candidate has progressed past sourcing on this search."
              />
            </ReportPanel>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <ReportPanel
              title="Rejection Reasons"
              // The denominator travels with the number. This is a share of the
              // candidates WITH A RECORDED REASON, not of all candidates, and
              // the three percentage widgets here each have a different basis.
              subtitle={`Share of the ${report.rejected_with_reason_total} candidates with a recorded reason`}
            >
              <LabeledBars
                buckets={report.rejection_reasons}
                emptyMessage="No rejection reasons recorded on this search."
                testId="rejection-bars"
              />
              {report.rejection_reasons_truncated ? (
                <p className="mt-2 text-caption text-text-secondary">
                  Showing the most common reasons only.
                </p>
              ) : null}
            </ReportPanel>
            {/* The DOCUMENT keeps prose subtitles where the live tab moved to
                info icons — a sentence that only exists on hover does not
                survive being printed. See `ReportPanel`'s own note. */}
            <ReportPanel title="Additions Over Time" subtitle="Candidates added by week">
              <AdditionsLine
                weeks={report.additions_by_week}
                emptyMessage="No candidate has a recorded creation date on this search."
                interactive={false}
                width={HALF_WIDTH}
              />
              {report.additions_outside_window_count > 0 ? (
                <p className="mt-2 text-caption text-text-secondary">
                  {report.additions_outside_window_count} added before this window.
                </p>
              ) : null}
            </ReportPanel>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <ReportPanel
              title="Where These Candidates Have Worked"
              subtitle="Companies these candidates have worked at, by number of candidates"
            >
              <LabeledBars
                buckets={report.top_companies ?? []}
                emptyMessage="No company is recorded for the candidates on this search."
                testId="top-companies-bars"
              />
            </ReportPanel>
            {/* WEEKLY, matching the tab. The bucket is a call-site decision
                rather than something the chart owns — Search Progress offers
                week or month over the same day series — so unlike the stage
                filter it has to be repeated here, deliberately. */}
            <ReportPanel title="Outreach Activity" subtitle="Outreaches per week, by method">
              <OutreachLines
                days={bucketOutreach(report.outreach_activity ?? [], "week")}
                pointLabel={(day) => bucketLabel(day, "week")}
                pointNoun="week"
                emptyMessage="No outreach recorded on this search."
                interactive={false}
                width={HALF_WIDTH}
              />
            </ReportPanel>
          </div>
        </>
      ) : progress ? (
        <>
          {/* The pickers were CONTROLS and cannot come along. What they were SET
              TO is information, and a document covering April while looking like
              it covers the year is the exact failure this line exists to
              prevent. */}
          <p className="text-caption text-text-secondary" data-testid="document-progress-range">
            {describeProgressView(progress)}
          </p>

          {/* THE EXPORT FOLLOWS THE DASHBOARD (the #742 rule), so the Title
              Case Jared asked for on 2026-08-25 has to reach the downloaded
              document too -- a report mailed to a client cannot carry different
              headings from the screen it was exported from. The casing rule
              itself lives in `charts/chartTitles.ts`; note the subtitles below
              keep the LOWERCASE `progress.granularity`, because they are
              prose. */}
          <ReportPanel
            title={`New Candidates In Each Stage By ${titleCaseNoun(progress.granularity)}`}
            subtitle={`Candidacies that entered each stage that ${progress.granularity}`}
          >
            <StageLines
              days={progress.daily.days}
              series={progress.daily.series}
              emptyMessage="No stage transitions in this range."
              testId="stage-new-lines"
              label={`New Candidates In Each Stage By ${titleCaseNoun(progress.granularity)}`}
              pointLabel={progress.pointLabel}
              pointNoun={progress.granularity}
              interactive={false}
              width={DOCUMENT_WIDTH - 48}
            />
          </ReportPanel>

          {/* Same header the tab shows since 2026-08-27 ("Total In Each Stage",
              Jared's words), so a downloaded chart and the screen it came from
              cannot be read as two different charts. The sentence under it stays
              PROSE here rather than moving into a tooltip: this document runs no
              JavaScript and gets printed, and a sentence that only exists on
              hover does not survive either. */}
          <ReportPanel
            title="Total In Each Stage"
            subtitle="Counts every candidate who ever entered the stage, including those who have since moved on"
          >
            <StageLines
              days={progress.daily.days}
              series={progress.cumulative}
              emptyMessage="No stage transitions in this range."
              testId="stage-total-lines"
              label="Total In Each Stage"
              pointLabel={progress.pointLabel}
              pointNoun={progress.granularity}
              interactive={false}
              width={DOCUMENT_WIDTH - 48}
            />
            {progress.carriedIn > 0 ? (
              // Reconciliation, not trivia: the totals start ABOVE zero and a
              // reader counting the points above will not reach them.
              <p
                data-testid="document-stage-baseline"
                className="mt-2 text-caption text-text-secondary"
              >
                {progress.carriedIn} stage {progress.carriedIn === 1 ? "entry" : "entries"} happened
                before this {progress.filtered ? "range" : "window"}.{" "}
                {progress.carriedIn === 1 ? "It is" : "They are"} already counted in the lines
                above.
              </p>
            ) : null}
          </ReportPanel>

          <ReportPanel
            title={`Outreaches By ${titleCaseNoun(progress.granularity)}`}
            subtitle={`Outreaches per ${progress.granularity}, by method`}
          >
            <OutreachLines
              days={progress.outreachDays}
              emptyMessage="No outreach in this range."
              testId="progress-outreach"
              pointLabel={progress.pointLabel}
              pointNoun={progress.granularity}
              interactive={false}
              width={DOCUMENT_WIDTH - 48}
            />
            {(report.outreach_outside_window_count ?? 0) + progress.droppedOutreach > 0 ? (
              <p
                data-testid="document-outreach-outside"
                className="mt-2 text-caption text-text-secondary"
              >
                {(report.outreach_outside_window_count ?? 0) + progress.droppedOutreach} logged
                before this {progress.filtered ? "range" : "window"}.
              </p>
            ) : null}
          </ReportPanel>
        </>
      ) : null}

      {/* ---- All Candidates ------------------------------------------------ */}

      {section === "dashboard" ? (
        <ReportPanel
          title="All Candidates"
          subtitle={`${candidates.length} ${candidates.length === 1 ? "candidate" : "candidates"}`}
        >
          {candidatesTruncated ? (
            <p
              data-testid="document-candidates-truncated"
              className="mb-2 text-caption text-warning-ink"
            >
              This search has more candidates than the cached table can hold, so the list below is
              short of the full set. The charts above still count every candidate.
            </p>
          ) : null}
          {/* The projection's columns. With `candidateDetails` the 2026-10-06
              ones join (location, rejection reason, the current stage's start
              date — epic 2026-10-06-search-candidates-reports, 02 — and, since
              05, the Thrive contact id the tab shows as a link, printed as the
              bare number: a document is read on paper as often as not, and
              the id is what a reader types into Thrive); without it this is
              the table the document always printed. A DATE rather
              than the screen's "N days" for the stage: a document is read
              later, and a day count frozen at download time would age
              silently while the date stays true. Rows cached before the
              fields existed print an em dash, like every other missing value.

              The rejection reason prints only on a Rejected row, as the
              screen shows it under the Rejected pill: Thrive keeps the field
              on a candidacy that later moved on (Rejected → On Hold), where it
              describes a decision that no longer stands. Reading it as "the
              reason this candidate is where they are" is the correct reading
              of the datum; printing a stale one beside an active stage is
              not. */}
          <table className="w-full border-collapse text-caption">
            <thead className="border-b border-border text-text-secondary">
              <tr>
                {(candidateDetails
                  ? [
                      "Name",
                      "Thrive",
                      "Location",
                      "Title",
                      "Company",
                      "Stage",
                      "Rejection reason",
                      "Highest stage",
                      "In stage since",
                      "Email",
                      "Added",
                    ]
                  : ["Name", "Title", "Company", "Stage", "Highest stage", "Email", "Added"]
                ).map((heading) => (
                  <th key={heading} scope="col" className="px-2 py-1.5 text-left">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {candidates.map((row, index) => (
                <tr
                  key={row.candidacy_id ?? `${row.name}-${row.email}-${index}`}
                  data-testid="document-candidate-row"
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-2 py-1.5 text-text">{row.name ?? "—"}</td>
                  {candidateDetails ? (
                    <td
                      data-testid="document-candidate-thrive-id"
                      className="whitespace-nowrap px-2 py-1.5 tabular-nums"
                    >
                      {row.contact_id ?? "—"}
                    </td>
                  ) : null}
                  {candidateDetails ? (
                    <td className="px-2 py-1.5">
                      {[row.city, row.state].filter(Boolean).join(", ") || "—"}
                    </td>
                  ) : null}
                  <td className="px-2 py-1.5">{row.title ?? "—"}</td>
                  <td className="px-2 py-1.5">{row.company ?? "—"}</td>
                  <td className="px-2 py-1.5">{row.stage ?? "—"}</td>
                  {candidateDetails ? (
                    <td className="px-2 py-1.5">
                      {row.stage === "Rejected" ? (row.rejection_reason ?? "—") : "—"}
                    </td>
                  ) : null}
                  <td className="px-2 py-1.5">{row.highest_stage ?? "—"}</td>
                  {candidateDetails ? (
                    <td className="whitespace-nowrap px-2 py-1.5">{formatDate(row.stage_since)}</td>
                  ) : null}
                  <td className="px-2 py-1.5">{row.email ?? "—"}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">{formatDate(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportPanel>
      ) : null}

      {/* ---- Provenance ---------------------------------------------------- */}

      <footer
        className="mt-2 flex flex-col gap-0.5 text-caption text-text-secondary"
        data-testid="document-provenance"
      >
        <p>
          Report generated {formatDate(report.generated_at)}; downloaded {generatedOn}.
          {stale ? " The report was past its daily refresh when this was taken." : ""}
        </p>
        <p>
          {/* The provenance has to describe THIS document's filters, and the two
              sections filter different things. A progress document that said
              "no filters applied" under a chart covering March would be the file
              contradicting itself — which is worse than saying nothing, because
              a reader has no reason to doubt a footer. */}
          {section === "progress"
            ? progress
              ? progress.filtered
                ? `Filtered to ${describeProgressView(progress)}`
                : `No range filter — ${describeProgressView(progress).toLowerCase()}`
              : "No progress data on this search."
            : appliedFilters.length === 0
              ? "No filters applied — this is every candidate on the search."
              : `Filtered by ${appliedFilters.map((f) => `${f.label}: ${f.value}`).join("; ")}.`}
        </p>
      </footer>
    </div>
  );
}
